import { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { Formik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Box, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { captureMessage } from '@sentry/react';

import {
  Button,
  PasswordField,
  TextField,
  Typography,
} from '@zendesk/forethought-elements';
import LoadingSpinner from '../../spinner';
import * as API from 'src/services/api';
import { passwordRegex } from 'src/utils/passwordUtils';

export const PASSWORD_CREATION_IN_PROGRESS_KEY =
  'password_creation_in_progress';

interface TempUser {
  challengeParam: {
    userAttributes: {
      email: string;
    };
  };
}

interface CreatePasswordFormProps {
  handleMagicLinkError: () => void;
  handleSuccess: () => void;
  oldPassword: string;
  title: string;
  username: string;
}

export function NewPasswordForm({
  email = '',
  errorMessage = '',
  handleSubmit,
  showCodeField = false,
  title,
}: {
  email?: string;
  errorMessage?: string;
  handleSubmit: (args: {
    code?: string;
    password1: string;
    password2: string;
  }) => void;
  showCodeField?: boolean;
  title: string;
}) {
  const { t } = useTranslation('authentication-wall');
  const theme = useTheme();

  const initialValues = {
    password1: '',
    password2: '',
    ...(showCodeField ? { code: '' } : {}),
  };

  const validationSchema = Yup.object().shape({
    password1: Yup.string()
      .required(t('common.fieldRequired'))
      .min(12, t('common.passwordMinLength'))
      .matches(
        passwordRegex,
        t('createPasswordForm.passwordDoesNotMeetRequirements'),
      ),
    password2: Yup.string().when('password1', {
      is: (val: string) => (val && val.length > 0 ? true : false),
      then: Yup.string().oneOf(
        [Yup.ref('password1')],
        t('createPasswordForm.passwordsDoNotMatch'),
      ),
    }),
    ...(showCodeField
      ? {
          code: Yup.string().required(t('common.fieldRequired')),
        }
      : {}),
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ errors, handleChange, handleSubmit, values }) => {
        return (
          <Form onSubmit={handleSubmit}>
            <h2>{title}</h2>
            {email && (
              <TextField
                disabled
                label={t('common.email')}
                type='text'
                value={email}
              />
            )}
            {showCodeField && (
              <TextField
                autoComplete='one-time-code'
                label={t('createPasswordForm.securityCode')}
                name='code'
                onChange={handleChange}
                placeholder={t('createPasswordForm.enterSecurityCode')}
                type='text'
                value={values.code || ''}
              />
            )}
            <PasswordField
              error={errors.password1}
              label={t('common.password')}
              name='password1'
              onChange={handleChange}
              placeholder={t('common.password')}
              type='password'
              value={values.password1}
            />
            <PasswordField
              error={errors.password2}
              label={t('common.confirmPassword')}
              name='password2'
              onChange={handleChange}
              placeholder={t('common.confirmPassword')}
              type='password'
              value={values.password2}
            />
            <Requirements>
              <b>{t('createPasswordForm.requirements.title')}</b>
              <ul>
                <li>{t('createPasswordForm.requirements.lowerUpperCase')}</li>
                <li>{t('createPasswordForm.requirements.number')}</li>
                <li>{t('createPasswordForm.requirements.specialCharacter')}</li>
                <li>{t('createPasswordForm.requirements.minLength')}</li>
              </ul>
            </Requirements>
            {errorMessage && (
              <Box>
                <Typography
                  color={theme.palette.colors.red[500]}
                  variant='font16'
                >
                  {errorMessage}
                </Typography>
              </Box>
            )}
            <Typography variant='font14'>
              <Trans
                components={{
                  bold: <Box component='span' sx={{ fontWeight: '600' }} />,
                  privacy: (
                    <a
                      href='https://forethought.ai/privacy-policy/'
                      rel='noreferrer'
                      target='_blank'
                    />
                  ),
                  saas: (
                    <a
                      href='https://forethought.ai/saas-agreement/'
                      rel='noreferrer'
                      target='_blank'
                    />
                  ),
                }}
                i18nKey='createPasswordForm.agreement'
                ns='authentication-wall'
              />
            </Typography>
            <Button fullWidth type='submit' variant='main'>
              {t('createPasswordForm.setPassword')}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
}

const CreatePasswordForm = ({
  handleMagicLinkError,
  handleSuccess,
  oldPassword,
  title,
  username,
}: CreatePasswordFormProps) => {
  const [user, setUser] = useState<TempUser | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    Auth.signIn(username, oldPassword)
      .then(setUser)
      .catch(reason => {
        captureMessage('Amplify Rejection in CreatePasswordForm', {
          extra: {
            amplifyRejection: reason,
          },
          level: 'info',
        });
        handleMagicLinkError();
      });
  }, [oldPassword, username, handleMagicLinkError]);

  const handleSubmit = async ({ password1 }: { password1: string }) => {
    setIsSubmitting(true);

    // set flag to prevent Hub listener from reacting to auth events during this sequence
    sessionStorage.setItem(PASSWORD_CREATION_IN_PROGRESS_KEY, 'true');

    // perform login then global logout to invalidate all previous sessions (security requirement)
    await Auth.completeNewPassword(user, password1);
    await Auth.signIn(username, password1);
    API.logoutUser();
    await Auth.signOut({ global: true });

    // remove the flag to allow regular events/redirects on re-authenticate with the new password
    sessionStorage.removeItem(PASSWORD_CREATION_IN_PROGRESS_KEY);
    await Auth.signIn(username, password1);

    handleSuccess();
  };

  if (!user || isSubmitting) {
    return <LoadingSpinner />;
  }

  const { email } = user.challengeParam.userAttributes;

  return (
    <NewPasswordForm email={email} handleSubmit={handleSubmit} title={title} />
  );
};

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Requirements = styled('div')`
  text-align: left;

  > ul {
    margin: 0;
    padding-inline-start: 24px;
  }
`;

export default CreatePasswordForm;
